import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { TabPane } from '../TabPane';
import React from 'react';
import { FieldArray } from 'react-final-form-arrays';
import { Field } from 'react-final-form';
import ZuulQueryBuilder from '../../QueryBuilder/ZuulQueryBuilder';
import { PercentageField } from '../../FinalForm';
import CollapsibleCard from '../../CollapsibleCard/CollapsibleCard';
import { randomFailureFields } from '../../../utils/queryBuilderFields';
import { useDomains } from '../../../views/OfferBlocks/queries';
import { useSites } from '../../../views/Sites/queries';
import { useAffiliates } from '../../../views/Affiliates/queries';
import ZuulLoader from '../../ZuulLoader/ZuulLoader';
import { RandomFailureProbabilityProps } from '../AnalysisPane';
import { Entity } from '../../../views/CustomProperties/EntityGroups/details/types';
import { useLandingPagesDtos } from '../../../views/LandingPages/queries';
import { useLandingPageGroupsDtos } from '../../../views/LandingPageGroups/queries';

const generateName = (field: string, source: string) => {
  return source ? `${source}.${field}` : field;
};

type AnalysisPaneProps = {
  source: string;
};

const AnalysisPane: React.FC<AnalysisPaneProps> = (props) => {
  return (
    <TabPane tabId='Analysis'>
      <Grid container spacing={2} sx={{ alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 12 }}>
          <RandomFailureProbabilities {...props} />
        </Grid>
      </Grid>
    </TabPane>
  );
};

const RandomFailureProbabilities = (props: AnalysisPaneProps) => {
  const { data: domains = [], isLoading: loadingDomains } = useDomains(
    true,
    (data: { id: any; name: any }[]): Entity[] =>
      data.map((domain: { id: any; name: any }) => ({
        id: domain.id,
        name: domain.name,
      }))
  );

  const { data: sites = [], isLoading: loadingSites } = useSites((data): Entity[] =>
    data.map((site: { id: any; domain: any }) => ({
      id: site.id,
      name: site.domain,
    }))
  );

  const { data: affiliates = [], isLoading: loadingAffiliates } = useAffiliates(true, (data): Entity[] =>
    data.map((affiliate: { id: any; name: any }) => ({
      id: affiliate.id,
      name: affiliate.name,
    }))
  );

  const { data: landingPages = [], isLoading: loadingLandingPages } = useLandingPagesDtos((data): Entity[] =>
    data.map((landingPage: any) => ({
      id: landingPage.id,
      name: landingPage.offer.name,
    }))
  );

  const { data: landingPageGroups = [], isLoading: loadingLandingPageGroups } = useLandingPageGroupsDtos(
    (data): Entity[] =>
      data.map((landingPageGroup: { id: any; name: any }) => ({
        id: landingPageGroup.id,
        name: landingPageGroup.name,
      }))
  );

  if (loadingDomains || loadingSites || loadingAffiliates || loadingLandingPages || loadingLandingPageGroups) {
    return <ZuulLoader text={'Loading Query Builder Fields...'} />;
  }

  return (
    <FieldArray name={generateName('randomFailureProbabilities', props.source)}>
      {({ fields }) => (
        <CollapsibleCard
          title={'Random Failure Probabilities'}
          defaultOpen={true}
          headerButton={
            <Tooltip title='Add a failure probability'>
              <IconButton
                color='success'
                aria-label='Add a failure probability'
                onClick={() => fields.push({ probability: 0, condition: undefined })}
              >
                <Add />
              </IconButton>
            </Tooltip>
          }
        >
          <Stack spacing={2}>
            {fields.map((name, index) => (
              <Card key={name} variant='outlined'>
                <CardHeader
                  slotProps={{ title: { component: 'div' } }}
                  title={
                    <Grid container spacing={2} sx={{ alignItems: 'center' }}>
                      <Grid size='grow'>
                        <PercentageField
                          name={generateName('probability', name)}
                          label='Failure Probability Percentage:'
                          inlineLabel
                        />
                      </Grid>
                      <Grid size='grow' sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Tooltip title='Remove this failure probability'>
                          <IconButton
                            color='error'
                            aria-label='Remove this failure probability'
                            onClick={() => fields.remove(index)}
                          >
                            <DeleteOutlined />
                          </IconButton>
                        </Tooltip>
                      </Grid>
                    </Grid>
                  }
                />
                <CardContent>
                  <RandomFailureProbability
                    name={name}
                    queryFields={randomFailureFields(domains, affiliates, sites, landingPages, landingPageGroups)}
                  />
                </CardContent>
              </Card>
            ))}
          </Stack>
        </CollapsibleCard>
      )}
    </FieldArray>
  );
};

const RandomFailureProbability = (props: RandomFailureProbabilityProps) => {
  return (
    <Grid container spacing={2} sx={{ alignItems: 'start' }}>
      <Grid size='grow'>
        <Field name={generateName('condition', props.name)} label='Condition'>
          {({ input, meta }) => (
            <ZuulQueryBuilder fields={props.queryFields} query={input.value} setQuery={input.onChange} />
          )}
        </Field>
      </Grid>
    </Grid>
  );
};

export default AnalysisPane;
